import isArray from 'lodash-es/isArray'
import * as Yup from 'yup'

type stepType = string | number | null

// `Yup.addMethod` types its callback as `(...args: any[])`, so every parameter
// below has to be annotated explicitly or it arrives as an implicit `any`.
Yup.addMethod(
  Yup.mixed,
  'forSteps',
  function (
    steps: stepType | stepType[],
    // Yup declares its base `Schema.required()` as returning `any`, so annotate
    // this as the concrete schema type the method is installed on (`Yup.mixed`)
    // to keep the chained result typed. The public contract in the
    // `declare module` block below stays `Yup.AnySchema` for callers.
    schema?: Yup.MixedSchema,
    testOptions: Yup.TestConfig = { test: () => true },
    requiredMessage?: string
  ) {
    return this.when('$step', {
      is: (step: stepType) =>
        isArray(steps) ? steps.includes(step) : steps === step,
      then: () => (schema || this).test(testOptions).required(requiredMessage),
      otherwise: () => this.optional().nullable(),
    })
  }
)

Yup.addMethod(Yup.mixed, 'requiredInStates', function (states: string[]) {
  return this.when('state', {
    is: (state: string) => states.includes(state),
    then: () => this.required(),
    otherwise: () => this.optional().nullable(),
  })
})

declare module 'yup' {
  interface StringSchema {
    forSteps(
      step: stepType | stepType[],
      schema?: Yup.AnySchema,
      testOptions?: {
        test: Yup.TestConfig
      },
      requiredMessage?: string
    ): Yup.StringSchema
  }

  interface ArraySchema<
    // This needs to match Yup's own ArraySchema signature
    // oxlint-disable-next-line typescript-eslint/no-explicit-any
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Yup.Flags = '',
    T = unknown,
    C = unknown,
    TOut = unknown,
  > {
    forSteps(
      step: stepType | stepType[],
      schema?: Yup.AnySchema,
      testOptions?: {
        test: Yup.TestConfig
      },
      requiredMessage?: string
    ): Yup.ArraySchema<TIn, TContext, TDefault, TFlags, T, C, TOut>
  }
}
